import { useRef } from "react";
import {
    motion,
    useMotionValue,
    useSpring,
    useTransform,
    useScroll,
} from "framer-motion";
import { ArrowDown, ArrowUpRight, MousePointer2, Figma, TrendingUp } from "lucide-react";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-2 -mb-2">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const float = (duration, delay = 0, distance = 10) => ({
    animate: { y: [0, -distance, 0] },
    transition: { duration, repeat: Infinity, ease: "easeInOut", delay },
});

export default function Hero() {
    const sceneRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 50, damping: 18 });
    const sy = useSpring(my, { stiffness: 50, damping: 18 });
    const ax = useTransform(sx, (v) => v * 22);
    const ay = useTransform(sy, (v) => v * 14);
    const bx = useTransform(sx, (v) => v * -16);
    const by = useTransform(sy, (v) => v * -10);
    const { scrollYProgress } = useScroll({
        target: sceneRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    const scrollToWork = () => {
        if (window.__lenis) window.__lenis.scrollTo("#work", { offset: -90 });
        else document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" });
    };

    const scrollToContact = (e) => {
        e.preventDefault();
        if (window.__lenis) window.__lenis.scrollTo("#contact", { offset: -60 });
        else document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="home"
            ref={sceneRef}
            onMouseMove={onMove}
            className="relative min-h-screen overflow-hidden pt-32 pb-20 lg:pt-36"
            data-testid="hero-section"
        >
            <div className="pointer-events-none absolute -top-48 -left-48 h-[36rem] w-[36rem] rounded-full bg-lav-200 opacity-80 blur-3xl" />
            <div className="pointer-events-none absolute top-1/3 -right-48 h-[32rem] w-[32rem] rounded-full bg-sun-soft opacity-40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 right-1/4 h-[26rem] w-[26rem] rounded-full bg-[#FFC7E8] opacity-30 blur-3xl" />
            <div
                className="pointer-events-none absolute inset-0 bg-dots opacity-40"
                style={{
                    maskImage: "radial-gradient(ellipse at 30% 40%, black 20%, transparent 70%)",
                    WebkitMaskImage: "radial-gradient(ellipse at 30% 40%, black 20%, transparent 70%)",
                }}
            />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-16">
                <div className="order-2 lg:order-1 lg:col-span-6">
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="mb-7 flex items-center gap-2 font-mono text-xs font-medium uppercase tracking-[0.3em] text-viol"
                        data-testid="hero-eyebrow"
                    >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-sun" />
                        Sushma Jaykar · UI/UX Designer
                    </motion.p>

                    <h1 className="text-[2.75rem] font-extrabold leading-[1.03] tracking-tight text-ink sm:text-6xl lg:text-[4.35rem]">
                        <MaskedLine delay={0.25}>Designing digital</MaskedLine>
                        <MaskedLine delay={0.37}>products that feel</MaskedLine>
                        <MaskedLine delay={0.49}>
                            <span className="relative inline-block">
                                <span className="relative z-10">effortless.</span>
                                <motion.span
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ duration: 0.7, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
                                    className="absolute bottom-1 left-0 z-0 h-4 w-full origin-left rounded-sm bg-sun sm:h-5"
                                />
                            </span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.85 }}
                        className="mt-8 max-w-md text-base leading-relaxed text-mist"
                        data-testid="hero-subtext"
                    >
                        UI/UX Designer with 5+ years of experience creating intuitive
                        digital experiences for Healthcare, Banking, Finance,
                        E-commerce, and Entertainment.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 1 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#contact"
                            onClick={scrollToContact}
                            className="btn-primary-grad group flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-transform duration-300 hover:-translate-y-0.5"
                            data-testid="hero-cta-contact"
                        >
                            Let's Connect
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                        <a
                            href="/resume/Sushma_Jaykar_CV_2026.pdf"
                            download
                            className="group flex items-center gap-1.5 rounded-full border-2 border-viol/30 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-viol transition-all duration-300 hover:border-viol hover:bg-white/70"
                            data-testid="hero-cta-resume"
                        >
                            Resume
                            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                        </a>
                    </motion.div>
                </div>

                <div className="relative order-1 lg:order-2 lg:col-span-6" data-testid="hero-scene">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="relative mx-auto max-w-lg lg:max-w-none"
                    >
                        <motion.div style={{ y: imgY }} className="relative">
                            <div className="absolute left-1/2 top-1/2 -z-10 h-[130%] w-[130%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-lav-200/80 blur-3xl" />
                            <div className="absolute -right-10 top-8 -z-10 h-56 w-56 rounded-full bg-viol/20 blur-3xl" />
                            <div className="absolute -left-8 bottom-4 -z-10 h-48 w-48 rounded-full bg-[#FFC7E8]/40 blur-3xl" />
                            <motion.div
                                animate={{ y: [0, -8, 0] }}
                                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <img
                                    src="/assets/hero.png"
                                    alt="Illustration of Sushma designing at her desk"
                                    className="h-auto w-full scale-[1.12] object-cover lg:scale-[1.22] lg:translate-x-4"
                                    style={{
                                        maskImage: "radial-gradient(ellipse 54% 56% at 50% 48%, black 46%, transparent 88%)",
                                        WebkitMaskImage: "radial-gradient(ellipse 54% 56% at 50% 48%, black 46%, transparent 88%)",
                                    }}
                                    data-testid="hero-illustration"
                                />
                            </motion.div>
                        </motion.div>

                        <motion.div style={{ x: ax, y: ay }} className="absolute -left-2 top-2 z-20 sm:-left-6">
                            <motion.div
                                {...float(5)}
                                className="glass flex rotate-[-4deg] items-center gap-2.5 rounded-2xl p-3 pr-5"
                                data-testid="hero-figma-card"
                            >
                                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-ink">
                                    <Figma className="h-5 w-5 text-sun" />
                                </span>
                                <div>
                                    <p className="text-xs font-bold text-ink">Component</p>
                                    <p className="font-mono text-[9px] uppercase tracking-widest text-mist">Auto-layout</p>
                                </div>
                            </motion.div>
                        </motion.div>

                        <motion.div style={{ x: bx, y: by }} className="absolute -right-1 top-24 z-20 sm:-right-8">
                            <motion.div
                                {...float(4.4, 0.6, 8)}
                                className="glass w-36 rotate-[3deg] rounded-2xl p-3.5"
                                data-testid="hero-wireframe-card"
                            >
                                <div className="flex gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-viol/50" />
                                    <span className="h-2 w-2 rounded-full bg-sun" />
                                    <span className="h-2 w-2 rounded-full bg-lav-300" />
                                </div>
                                <div className="mt-2.5 h-2 w-3/4 rounded-full bg-ink/15" />
                                <div className="mt-1.5 h-2 w-1/2 rounded-full bg-ink/10" />
                                <div className="mt-2.5 flex gap-1.5">
                                    <div className="h-8 flex-1 rounded-lg bg-lav-200" />
                                    <div className="h-8 flex-1 rounded-lg bg-viol/25" />
                                </div>
                            </motion.div>
                        </motion.div>

                        <motion.div style={{ x: ax, y: by }} className="absolute -left-1 bottom-24 z-20 sm:-left-9">
                            <motion.div
                                {...float(5.6, 1, 9)}
                                className="glass rotate-[2deg] rounded-2xl p-3.5"
                                data-testid="hero-design-system-card"
                            >
                                <p className="mb-2 font-mono text-[9px] uppercase tracking-widest text-mist">Design System</p>
                                <div className="flex items-center gap-1.5">
                                    <span className="h-6 w-6 rounded-lg bg-viol ring-1 ring-white" />
                                    <span className="h-6 w-6 rounded-lg bg-lav-200 ring-1 ring-white" />
                                    <span className="h-6 w-6 rounded-lg bg-sun ring-1 ring-white" />
                                    <span className="h-6 w-6 rounded-full bg-ink ring-1 ring-white" />
                                </div>
                                <div className="mt-2 flex items-center gap-1.5">
                                    <span className="rounded-md bg-viol/10 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-viol">radius/16</span>
                                    <span className="rounded-md bg-sun/30 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-ink">space/8</span>
                                </div>
                            </motion.div>
                        </motion.div>

                        <motion.div style={{ x: bx, y: ay }} className="absolute -right-2 bottom-32 z-20 sm:-right-6">
                            <motion.div
                                {...float(4.8, 0.3, 8)}
                                className="glass w-32 rotate-[-2deg] rounded-2xl p-3"
                                data-testid="hero-analytics-card"
                            >
                                <div className="flex items-center justify-between">
                                    <p className="font-mono text-[9px] uppercase tracking-widest text-mist">Usability</p>
                                    <TrendingUp className="h-3 w-3 text-viol" />
                                </div>
                                <div className="mt-2 flex items-end gap-1">
                                    <span className="h-3 w-2.5 rounded-sm bg-lav-300" />
                                    <span className="h-5 w-2.5 rounded-sm bg-viol/40" />
                                    <span className="h-4 w-2.5 rounded-sm bg-lav-300" />
                                    <span className="h-7 w-2.5 rounded-sm bg-viol" />
                                    <span className="h-9 w-2.5 rounded-sm bg-sun" />
                                </div>
                                <p className="mt-1.5 text-[10px] font-extrabold text-ink">+94% task success</p>
                            </motion.div>
                        </motion.div>

                        <motion.div style={{ x: bx, y: ay }} className="absolute -bottom-3 right-10 z-20 sm:right-16">
                            <motion.div
                                animate={{ rotate: [-3, 0, -3] }}
                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                                className="w-32 -rotate-3 rounded-xl rounded-br-3xl bg-sun p-3.5 shadow-[0_10px_25px_rgba(234,179,8,0.35)]"
                                data-testid="hero-sticky-note"
                            >
                                <p className="hand text-lg leading-tight text-ink">User first, always ✦</p>
                            </motion.div>
                        </motion.div>

                        <motion.div style={{ x: ax, y: ay }} className="absolute right-6 -top-2 z-20 sm:right-12">
                            <motion.div
                                {...float(3.8, 1.4, 7)}
                                className="flex items-center gap-1.5"
                                data-testid="hero-cursor"
                            >
                                <MousePointer2 className="h-5 w-5 fill-viol text-viol" />
                                <span className="glass rounded-full rounded-bl-sm px-3 py-1.5 text-xs font-bold text-ink">
                                    Sushma
                                </span>
                            </motion.div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.6, duration: 0.8 }}
                            className="absolute -bottom-10 left-2 hidden items-center gap-2 lg:flex"
                            aria-hidden="true"
                        >
                            <span className="hand rotate-[-4deg] text-2xl text-viol">hi, that's me!</span>
                            <svg width="46" height="34" viewBox="0 0 46 34" fill="none">
                                <motion.path
                                    d="M2 4 C 16 26, 30 30, 43 22 M 43 22 l -8 -1 M 43 22 l -2 -8"
                                    stroke="#7047F5"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ delay: 1.8, duration: 0.9, ease: "easeOut" }}
                                />
                            </svg>
                        </motion.div>

                        <motion.svg
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 2, duration: 0.8 }}
                            className="pointer-events-none absolute -top-8 left-1/3 hidden lg:block"
                            width="72"
                            height="40"
                            viewBox="0 0 72 40"
                            fill="none"
                            aria-hidden="true"
                        >
                            <motion.path
                                d="M4 32 C 20 6, 44 4, 68 14"
                                stroke="#7047F5"
                                strokeOpacity="0.4"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeDasharray="4 6"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ delay: 2.1, duration: 1, ease: "easeOut" }}
                            />
                        </motion.svg>
                    </motion.div>
                </div>
            </div>

            <motion.button
                onClick={scrollToWork}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 0.8 }}
                className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-mist transition-colors hover:text-ink lg:flex"
                data-testid="hero-scroll-cue"
                aria-label="Scroll to work"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
                    <ArrowDown className="h-4 w-4" />
                </motion.span>
            </motion.button>
        </section>
    );
}
